import { RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/button";
import { Input } from "../../../components/input";
import { SelectV2 } from "../../../components/select-v2";
import { SyncPointsTable } from "../components/SyncPointsTable";
import { useSyncPoints } from "../hooks/useSyncPoints";
import type { SyncPointsStatus } from "../types/syncPoints.types";

export default function SyncPointsPage() {
  const { t } = useTranslation();
  const {
    items,
    totalCount,
    page,
    name,
    status,
    error,
    loading,
    refetch,
    setPage,
    updateName,
    updateStatus,
    pageSize,
  } = useSyncPoints();
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  return (
    <main className="p-4 md:p-6">
      <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("sync_points.title")}
          </h1>
          <p className="mt-1 text-sm text-gray-500">{t("sync_points.description")}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => void refetch()}
          disabled={loading}
        >
          <RefreshCw className={loading ? "mr-2 h-4 w-4 animate-spin" : "mr-2 h-4 w-4"} />
          {t("common.refresh")}
        </Button>
      </div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <Input
          value={name}
          onChange={(event) => updateName(event.target.value)}
          placeholder={t("sync_points.search")}
          className="w-full max-w-[300px]"
        />
        <SelectV2
          value={status}
          onChange={(value) => updateStatus(value as SyncPointsStatus)}
          options={[
            { value: "all", label: t("sync_points.all") },
            { value: "in_wallet_transaction", label: t("sync_points.in_wallet_transaction") },
            {
              value: "not_in_wallet_transaction",
              label: t("sync_points.not_in_wallet_transaction"),
            },
          ]}
          placeholder={t("sync_points.wallet_status")}
          ariaLabel={t("sync_points.wallet_status")}
          className="w-full sm:w-72"
        />
      </div>
      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
          <p>{error}</p>
          <Button
            className="mt-3"
            type="button"
            variant="outline"
            onClick={() => void refetch()}
          >
            {t("common.retry")}
          </Button>
        </div>
      ) : loading && items.length === 0 ? (
        <p className="py-12 text-center text-sm text-gray-500">{t("common.loading")}</p>
      ) : (
        <SyncPointsTable
          items={items}
          loading={loading}
          error={null}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          onRetry={() => void refetch()}
        />
      )}
    </main>
  );
}
